import { useState } from 'react';
import { Button, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { OcifXlsxFieldChange, OcifXlsxReplacementInfo } from '../../../api/OcifXlsxApi';
import { useScheduleTitle } from './FinancialResultPanel';
import { AccordionItem } from '../../ui/Accordion';
import { useDarkMode } from '../../../context/DarkModeContext';

function ScheduleChangesGrid({
  schedule,
  changes,
  itemId,
  isOpen,
  toggle,
}: {
  schedule: string;
  changes: OcifXlsxFieldChange[];
  itemId: string;
  isOpen: boolean;
  toggle: (id: string) => void;
}) {
  const { isDarkMode } = useDarkMode();
  return (
    <AccordionItem
      className="mb-2"
      itemId={itemId}
      isOpen={isOpen}
      toggle={toggle}
      title={
        <div className="flex items-center gap-2">
          <strong>{schedule}</strong>
          <span className="badge bg-warning">{changes.length}</span>
        </div>
      }
    >
      <div style={{ maxHeight: '16rem', overflowY: 'auto' }}>
        <Table borderless striped size="sm" className="mb-0" style={{ fontSize: '0.78rem', height: '100%', overflow: 'auto' }}>
          <thead style={{ position: 'sticky', top: 0, background: isDarkMode ? '' : 'white', zIndex: 1 }}>
            <tr>
              <th className="text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Account</Trans>
              </th>
              <th className="text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Field</Trans>
              </th>
              <th className="text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>Current value</Trans>
              </th>
              <th className="text-muted font-normal" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                <Trans>New value</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c, i) => (
              <tr key={i}>
                <td>
                  <code>{c.fieldCode}</code>
                </td>
                <td>{c.description}</td>
                <td className="text-muted" style={{ fontStyle: c.oldValue ? 'normal' : 'italic' }}>
                  {c.oldValue || <Trans>empty</Trans>}
                </td>
                <td className={c.newValue !== c.oldValue ? 'font-bold text-warning' : ''}>{c.newValue || <Trans>empty</Trans>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </AccordionItem>
  );
}

interface ReplaceModalProps {
  fileName: string;
  info: OcifXlsxReplacementInfo;
  rowCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ReplaceWarningModal({ fileName, info, rowCount, onConfirm, onCancel }: ReplaceModalProps) {
  const getTitle = useScheduleTitle();
  const [openSection, setOpenSection] = useState('');
  const toggle = (id: string) => setOpenSection(prev => (prev === id ? '' : id));

  return (
    <Modal isOpen backdrop="static" keyboard={false} size="lg">
      <ModalHeader>
        <Trans>Replace Existing Schedule Data?</Trans>
      </ModalHeader>
      <ModalBody>
        <p className="small text-muted mb-3">
          <strong>{fileName}</strong> · {rowCount} <Trans>rows</Trans>
        </p>
        <div className="mb-3">
          <strong className="text-danger small uppercase block mb-2">
            <Trans>To be replaced</Trans>
          </strong>
          {info.schedulesToReplace.map(r => (
            <ScheduleChangesGrid
              key={r.scheduleName}
              schedule={getTitle(r.scheduleName)}
              changes={r.fieldChanges ?? []}
              itemId={r.scheduleName}
              isOpen={openSection === r.scheduleName}
              toggle={toggle}
            />
          ))}
        </div>
        {info.unchangedSchedules.length > 0 && (
          <div>
            <strong className="small uppercase text-muted">
              <Trans>Unchanged (not in this file)</Trans>
            </strong>
            <ListGroup flush className="mt-1 small">
              {info.unchangedSchedules.map(s => (
                <ListGroupItem key={s} className="px-0 py-1">
                  {getTitle(s)}
                </ListGroupItem>
              ))}
            </ListGroup>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button color="danger" onClick={onConfirm}>
          <Trans>Confirm &amp; Replace</Trans>
        </Button>
      </ModalFooter>
    </Modal>
  );
}
